import * as React from 'react';
import PropTypes from 'prop-types';
import { itIT } from '@mui/material/locale';
import { createTheme } from '@mui/material/styles';
import { Box, useTheme, IconButton } from '@mui/material';
import {
  FirstPage as FirstPageIcon,
  LastPage as LastPageIcon,
  KeyboardArrowLeft,
  KeyboardArrowRight,
} from '@mui/icons-material';

import API from '../api';

/**
 * @param {any} props
 * @returns Table Pagination UI - Footer
 */
export function TablePaginationActions(props) {
  const theme = useTheme();
  const { count, page, rowsPerPage, onPageChange } = props;

  // handle initial page button click function
  const handleFirstPageButtonClick = (event) => {
    onPageChange(event, 0);
  };

  // handle previous page button click function
  const handleBackButtonClick = (event) => {
    onPageChange(event, page - 1);
  };

  // handle next page button click function
  const handleNextButtonClick = (event) => {
    onPageChange(event, page + 1);
  };

  // handle last page button click function
  const handleLastPageButtonClick = (event) => {
    onPageChange(event, Math.max(0, Math.ceil(count / rowsPerPage) - 1));
  };

  return (
    <Box sx={{ flexShrink: 0, ml: 2.5 }}>
      <IconButton onClick={handleFirstPageButtonClick} disabled={page === 0} aria-label="first page">
        {theme.direction === 'rtl' ? <LastPageIcon /> : <FirstPageIcon />}
      </IconButton>
      <IconButton onClick={handleBackButtonClick} disabled={page === 0} aria-label="previous page">
        {theme.direction === 'rtl' ? <KeyboardArrowRight /> : <KeyboardArrowLeft />}
      </IconButton>
      <IconButton
        onClick={handleNextButtonClick}
        disabled={page >= Math.ceil(count / rowsPerPage) - 1}
        aria-label="next page"
      >
        {theme.direction === 'rtl' ? <KeyboardArrowLeft /> : <KeyboardArrowRight />}
      </IconButton>
      <IconButton
        onClick={handleLastPageButtonClick}
        disabled={page >= Math.ceil(count / rowsPerPage) - 1}
        aria-label="last page"
      >
        {theme.direction === 'rtl' ? <FirstPageIcon /> : <LastPageIcon />}
      </IconButton>
    </Box>
  );
}

// Table Pagination input parameter type definitions
TablePaginationActions.propTypes = {
  count: PropTypes.number.isRequired,
  onPageChange: PropTypes.func.isRequired,
  page: PropTypes.number.isRequired,
  rowsPerPage: PropTypes.number.isRequired,
};

// Custom theme style in locale language
export const theme = createTheme(
  {
    palette: {
      primary: { main: '#1976d2' },
    },
  },
  itIT
);

/**
 *
 * @param {any} props
 * @returns Custom table pagination UI - Footer with HTTP api requests
 */
export function PaginationAPI(props) {
  const theme = useTheme();
  const { count, page, rowsPerPage, onPageChange, apiResponseData, setApiResponseData, lastPage, elements } = props;

  // HTTP Get Api request
  const apiCall = (url, event, change) => {
    API.get(url).then((response) => {
      setApiResponseData(response.data);
      onPageChange(event, change);
    });
  };

  // Handle initial page button request with appropriate api call
  const handleFirstPageButtonClick = (event) => {
    if (page > 0) {
      const updatePage = 0;
      apiCall(apiResponseData.first_page_url, event, updatePage);
    }
  };

  // Handle previous page button request with appropriate api call
  const handleBackButtonClick = (event) => {
    const updatePage = page - 1;

    if (page > 0) {
      if (rowsPerPage === 5) {
        if (page % 2 === 0) {
          apiCall(apiResponseData.prev_page_url, event, updatePage);
        } else {
          onPageChange(event, updatePage);
        }
      } else if (rowsPerPage === elements) {
        apiCall(apiResponseData.prev_page_url, event, updatePage);
      }
    }
  };

  // Handle next page button request with appropriate api call
  const handleNextButtonClick = (event) => {
    const updatePage = page + 1;

    if (rowsPerPage === 5) {
      if (page % 2 !== 0) {
        apiCall(apiResponseData.next_page_url, event, updatePage);
      } else {
        onPageChange(event, updatePage);
      }
    } else if (rowsPerPage === elements) {
      apiCall(apiResponseData.next_page_url, event, updatePage);
    }
  };

  // Handle last page button request with appropriate api call
  const handleLastPageButtonClick = (event) => {
    if (page < lastPage) {
      const updatePage = Math.max(0, Math.ceil(count / rowsPerPage) - 1);
      apiCall(apiResponseData.last_page_url, event, updatePage);
    }
  };

  return (
    <Box sx={{ flexShrink: 0, ml: 2.5, paddingRight: '14px' }}>
      <IconButton onClick={handleFirstPageButtonClick} disabled={page === 0} aria-label="first page">
        {theme.direction === 'rtl' ? <LastPageIcon /> : <FirstPageIcon />}
      </IconButton>
      <IconButton onClick={handleBackButtonClick} disabled={page === 0} aria-label="previous page">
        {theme.direction === 'rtl' ? <KeyboardArrowRight /> : <KeyboardArrowLeft />}
      </IconButton>
      <IconButton
        onClick={handleNextButtonClick}
        disabled={page >= Math.ceil(count / rowsPerPage) - 1}
        aria-label="next page"
      >
        {theme.direction === 'rtl' ? <KeyboardArrowLeft /> : <KeyboardArrowRight />}
      </IconButton>
      <IconButton
        onClick={handleLastPageButtonClick}
        disabled={page >= Math.ceil(count / rowsPerPage) - 1}
        aria-label="last page"
      >
        {theme.direction === 'rtl' ? <FirstPageIcon /> : <LastPageIcon />}
      </IconButton>
    </Box>
  );
}

// Pagination api input parameter type definition
PaginationAPI.propTypes = {
  onPageChange: PropTypes.func.isRequired,
  page: PropTypes.number.isRequired,
  rowsPerPage: PropTypes.number.isRequired,
};

export function EntityFilterPagination(props) {
  const theme = useTheme();
  const { count, page, rowsPerPage, onPageChange, apiResponseData, setApiResponseData, lastPage, apiRequest } = props;

  // HTTP Get Api request
  const apiCall = (toPage, event, change) => {
    API.get(`${apiRequest}&page=${toPage}`).then((response) => {
      setApiResponseData(response.data);
      onPageChange(event, change);
    });
  };

  // Handle initial page button request with appropriate api call
  const handleFirstPageButtonClick = (event) => {
    if (page > 0) {
      const updatePage = 0;
      apiCall(updatePage, event, updatePage);
    }
  };

  // Handle previous page button request with appropriate api call
  const handleBackButtonClick = (event) => {
    const updatePage = page - 1;

    if (page > 0) {
      if (rowsPerPage === 10) {
        if (page % 2 === 0) {
          apiCall(apiResponseData.current_page - 1, event, updatePage);
        } else {
          onPageChange(event, updatePage);
        }
      }
    }
  };

  // Handle next page button request with appropriate api call
  const handleNextButtonClick = (event) => {
    const updatePage = page + 1;

    if (rowsPerPage === 10) {
      if (page % 2 !== 0) {
        apiCall(apiResponseData.current_page + 1, event, updatePage);
      } else {
        onPageChange(event, updatePage);
      }
    }
  };

  // Handle last page button request with appropriate api call
  const handleLastPageButtonClick = (event) => {
    if (page < lastPage) {
      const updatePage = Math.max(0, Math.ceil(count / rowsPerPage) - 1);
      apiCall(apiResponseData.last_page, event, updatePage);
    }
  };

  return (
    <Box sx={{ flexShrink: 0, ml: 2.5, paddingRight: '14px' }}>
      <IconButton onClick={handleFirstPageButtonClick} disabled={page === 0} aria-label="first page">
        {theme.direction === 'rtl' ? <LastPageIcon /> : <FirstPageIcon />}
      </IconButton>
      <IconButton onClick={handleBackButtonClick} disabled={page === 0} aria-label="previous page">
        {theme.direction === 'rtl' ? <KeyboardArrowRight /> : <KeyboardArrowLeft />}
      </IconButton>
      <IconButton
        onClick={handleNextButtonClick}
        disabled={page >= Math.ceil(count / rowsPerPage) - 1}
        aria-label="next page"
      >
        {theme.direction === 'rtl' ? <KeyboardArrowLeft /> : <KeyboardArrowRight />}
      </IconButton>
      <IconButton
        onClick={handleLastPageButtonClick}
        disabled={page >= Math.ceil(count / rowsPerPage) - 1}
        aria-label="last page"
      >
        {theme.direction === 'rtl' ? <FirstPageIcon /> : <LastPageIcon />}
      </IconButton>
    </Box>
  );
}

// Pagination api input parameter type definition
EntityFilterPagination.propTypes = {
  count: PropTypes.number.isRequired,
  onPageChange: PropTypes.func.isRequired,
  page: PropTypes.number.isRequired,
  rowsPerPage: PropTypes.number.isRequired,
};

export function UserFilterPagination(props) {
  const theme = useTheme();
  const { count, page, rowsPerPage, onPageChange, apiResponseData, setApiResponseData, lastPage, apiRequest } = props;

  // HTTP Get Api request
  const apiCall = (toPage, event, change) => {

    API.get(`${apiRequest}&page=${toPage}`).then((response) => {
      setApiResponseData(response.data);
      onPageChange(event, change);
    });
  };

  // Handle initial page button request with appropriate api call
  const handleFirstPageButtonClick = (event) => {
    if (page > 0) {
      const updatePage = 0;
      apiCall(updatePage, event, updatePage);
    }
  };

  // Handle previous page button request with appropriate api call
  const handleBackButtonClick = (event) => {
    const updatePage = page - 1;

    if (page > 0) {
      if (rowsPerPage === 10) {
        if (page % 2 === 0) {
          apiCall(apiResponseData.page - 1, event, updatePage);
        } else {
          onPageChange(event, updatePage);
        }
      }
    }
  };

  // Handle next page button request with appropriate api call
  const handleNextButtonClick = (event) => {
    const updatePage = page + 1;

    if (rowsPerPage === 10) {
      if (page % 2 !== 0) {
        apiCall(apiResponseData.page + 1, event, updatePage);
      } else {
        onPageChange(event, updatePage);
      }
    }
  };

  // Handle last page button request with appropriate api call
  const handleLastPageButtonClick = (event) => {
    if (page < lastPage) {
      const updatePage = Math.max(0, Math.ceil(count / rowsPerPage) - 1);
      apiCall(apiResponseData.last_page, event, updatePage);
    }
  };

  return (
    <Box sx={{ flexShrink: 0, ml: 2.5, paddingRight: '14px' }}>
      <IconButton onClick={handleFirstPageButtonClick} disabled={page === 0} aria-label="first page">
        {theme.direction === 'rtl' ? <LastPageIcon /> : <FirstPageIcon />}
      </IconButton>
      <IconButton onClick={handleBackButtonClick} disabled={page === 0} aria-label="previous page">
        {theme.direction === 'rtl' ? <KeyboardArrowRight /> : <KeyboardArrowLeft />}
      </IconButton>
      <IconButton
        onClick={handleNextButtonClick}
        disabled={page >= Math.ceil(count / rowsPerPage) - 1}
        aria-label="next page"
      >
        {theme.direction === 'rtl' ? <KeyboardArrowLeft /> : <KeyboardArrowRight />}
      </IconButton>
      <IconButton
        onClick={handleLastPageButtonClick}
        disabled={page >= Math.ceil(count / rowsPerPage) - 1}
        aria-label="last page"
      >
        {theme.direction === 'rtl' ? <FirstPageIcon /> : <LastPageIcon />}
      </IconButton>
    </Box>
  );
}

// Pagination api input parameter type definition
UserFilterPagination.propTypes = {
  count: PropTypes.number.isRequired,
  onPageChange: PropTypes.func.isRequired,
  page: PropTypes.number.isRequired,
  rowsPerPage: PropTypes.number.isRequired,
};
//   const theme = useTheme();
//   const { count, page, rowsPerPage, onPageChange, apiResponseData, setApiResponseData, lastPage } = props;

//   // HTTP Get Api request
//   const apiCall = (url, event, change) => {
//     API.get(url).then((response) => {
//       setApiResponseData(response.data);
//       onPageChange(event, change);
//     });
//   };

//   // Handle initial page button request with appropriate api call
//   const handleFirstPageButtonClick = (event) => {
//     if (page > 0) {
//       const updatePage = 0;
//       apiCall(apiResponseData.first_page_url, event, updatePage);
//     }
//   };

//   // Handle previous page button request with appropriate api call
//   const handleBackButtonClick = (event) => {
//     const updatePage = page - 1;

//     if (page > 0) {
//       if (rowsPerPage === 5) {
//         if (page % 2 === 0) {
//           apiCall(apiResponseData.prev_page_url, event, updatePage);
//         } else {
//           onPageChange(event, updatePage);
//         }
//       } else if (rowsPerPage === 50) {
//         apiCall(apiResponseData.prev_page_url, event, updatePage);
//       }
//     }
//   };

//   // Handle next page button request with appropriate api call
//   const handleNextButtonClick = (event) => {
//     const updatePage = page + 1;

//     if (rowsPerPage === 5) {
//       if (page % 2 !== 0) {
//         apiCall(apiResponseData.next_page_url, event, updatePage);
//       } else {
//         onPageChange(event, updatePage);
//       }
//     } else if (rowsPerPage === 50) {
//       apiCall(apiResponseData.next_page_url, event, updatePage);
//     }
//   };

//   // Handle last page button request with appropriate api call
//   const handleLastPageButtonClick = (event) => {
//     if (page < lastPage) {
//       const updatePage = Math.max(0, Math.ceil(count / rowsPerPage) - 1);
//       apiCall(apiResponseData.last_page_url, event, updatePage);
//     }
//   };

//   return (
//     <Box sx={{ flexShrink: 0, ml: 2.5, paddingRight: '14px' }}>
//       <IconButton onClick={handleFirstPageButtonClick} disabled={page === 0} aria-label="first page">
//         {theme.direction === 'rtl' ? <LastPageIcon /> : <FirstPageIcon />}
//       </IconButton>
//       <IconButton onClick={handleBackButtonClick} disabled={page === 0} aria-label="previous page">
//         {theme.direction === 'rtl' ? <KeyboardArrowRight /> : <KeyboardArrowLeft />}
//       </IconButton>
//       <IconButton
//         onClick={handleNextButtonClick}
//         disabled={page >= Math.ceil(count / rowsPerPage) - 1}
//         aria-label="next page"
//       >
//         {theme.direction === 'rtl' ? <KeyboardArrowLeft /> : <KeyboardArrowRight />}
//       </IconButton>
//       <IconButton
//         onClick={handleLastPageButtonClick}
//         disabled={page >= Math.ceil(count / rowsPerPage) - 1}
//         aria-label="last page"
//       >
//         {theme.direction === 'rtl' ? <FirstPageIcon /> : <LastPageIcon />}
//       </IconButton>
//     </Box>
//   );
// }

// // Pagination api input parameter type definition
// CityFCPaginationAPI.propTypes = {
//   onPageChange: PropTypes.func.isRequired,
//   page: PropTypes.number.isRequired,
//   rowsPerPage: PropTypes.number.isRequired,
// };